import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { GuideProgress } from '@/types';
import { GuideStep, GuideStepType } from '@/services/apis/guides';
import GuideViewerStepCard from './GuideViewerStepCard';

interface GuideScrollViewProps {
  steps: GuideStep[];
  progress: GuideProgress | null;
  onStepComplete: (stepIndex: number, forceComplete?: boolean) => void;
  onStepInView: (stepIndex: number) => void;
  getStepTranslation: (step: GuideStep) => any;
  guideId: string;
  isSavingComplete?: boolean;
}

const GuideScrollView: React.FC<GuideScrollViewProps> = ({
  steps,
  progress,
  onStepComplete,
  onStepInView,
  getStepTranslation,
  guideId,
  isSavingComplete = false,
}) => {
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Track which step is in view
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -20% 0px',
      threshold: 0.5,
    };

    const observers = stepRefs.current.map((ref, index) => {
      if (!ref) return null;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onStepInView(index);
          }
        });
      }, observerOptions);

      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, [steps.length, onStepInView]);

  return (
    <div className="md:max-w-3xl 2xl:max-w-5xl m-auto">
      <div className="space-y-8">
        {steps.map((step, index) => {
          const isCompleted = progress?.completedStepIds.includes(step.id) || false;

          return (
            <motion.div
              key={step.id}
              ref={(el) => {
                stepRefs.current[index] = el;
              }}
              className="min-h-[80vh] flex items-center py-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, margin: '-10%' }}
              transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <GuideViewerStepCard
                step={step}
                stepIndex={index}
                totalSteps={steps.length}
                isCompleted={isCompleted}
                onComplete={() => onStepComplete(index, step.type === GuideStepType.QUESTION)}
                onNext={() => onStepInView(index)}
                onPrevious={() => onStepInView(index)}
                getStepTranslation={getStepTranslation}
                guideId={guideId}
                progress={progress}
                isSavingComplete={isSavingComplete}
              />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default GuideScrollView;
